// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Grid, Group, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import type { Composition, DiagnosticReport } from '@medplum/fhirtypes';
import { IconClipboardText, IconFileDescription } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';
import classes from './SupplementalInformationSection.module.css';

export interface SupplementalInformationSectionProps {
  intakeComposition: Composition | undefined;
  hsatReports: DiagnosticReport[];
  loading: boolean;
  intakeQuestionnaireDeferred: boolean;
}

export function SupplementalInformationSection({
  intakeComposition,
  hsatReports,
  loading,
  intakeQuestionnaireDeferred,
}: SupplementalInformationSectionProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Title order={2} mb="md">
        Supplemental Information
      </Title>
      <Grid>
        {intakeQuestionnaireDeferred && (
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper withBorder radius="md" p="md" className={classes.card}>
              <Group align="flex-start" wrap="nowrap">
                <ThemeIcon size="xl" radius="md" variant="light" color="polaris">
                  <IconClipboardText size={24} />
                </ThemeIcon>
                <Stack gap={4} style={{ flex: 1 }}>
                  <Text fw={600}>Intake Questionnaire</Text>
                  <Anchor
                    component="button"
                    type="button"
                    onClick={() => navigate('/?openIntakeQuestionnaire=true')}
                    size="sm"
                    fw={500}
                  >
                    Complete Questionnaire
                  </Anchor>
                </Stack>
              </Group>
            </Paper>
          </Grid.Col>
        )}
        {!intakeQuestionnaireDeferred && (
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper withBorder radius="md" p="md" className={classes.card}>
              <Group align="flex-start" wrap="nowrap">
                <ThemeIcon size="xl" radius="md" variant="light" color="polaris">
                  <IconClipboardText size={24} />
                </ThemeIcon>
                <Stack gap={4} style={{ flex: 1 }}>
                  <Text fw={600}>Intake Screener</Text>
                  {renderLink(loading, Boolean(intakeComposition?.id), 'View Screener', 'Not captured yet', () =>
                    navigate('/my-chart/intake-screener')
                  )}
                </Stack>
              </Group>
            </Paper>
          </Grid.Col>
        )}
        {(loading || hsatReports.length > 0) && (
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper withBorder radius="md" p="md" className={classes.card}>
              <Group align="flex-start" wrap="nowrap">
                <ThemeIcon size="xl" radius="md" variant="light" color="polaris">
                  <IconFileDescription size={24} />
                </ThemeIcon>
                <Stack gap={4} style={{ flex: 1 }}>
                  <Text fw={600}>HSAT Results</Text>
                  {renderLink(loading, hsatReports.length > 0, 'View Report', 'No results on file', () =>
                    navigate('/my-chart/results')
                  )}
                </Stack>
              </Group>
            </Paper>
          </Grid.Col>
        )}
      </Grid>
    </Paper>
  );
}

function renderLink(
  loading: boolean,
  available: boolean,
  linkLabel: string,
  emptyLabel: string,
  onClick: () => void
): JSX.Element {
  if (loading) {
    return (
      <Text size="sm" c="dimmed">
        Loading…
      </Text>
    );
  }
  if (available) {
    return (
      <Anchor component="button" type="button" onClick={onClick} size="sm" fw={500}>
        {linkLabel}
      </Anchor>
    );
  }
  return (
    <Text size="sm" c="dimmed">
      {emptyLabel}
    </Text>
  );
}
